import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  UserRound, Heart, ShoppingBag, BellRing, Truck, Phone,
  MapPin, Timer, Leaf, ExternalLink, ChevronRight, ArrowLeft,
  Pencil, Smartphone, Home, CheckCircle, Send, Building2,
} from 'lucide-react';
import { parseUTC, formatDateShortTime } from '../../utils/date';
import { ORDER_STATUS_LABELS } from '../../constants/statuses';
import { GARDEN_CENTER } from '../../constants/garden';
import { cleanPhoneInput, formatPhoneDisplay, normalizePhone, isPhoneComplete } from '../../utils/phone';
import { formatPrice } from '../../utils/price';
import { hasDiscount, priceBefore } from '../../utils/discount';

// Иконка Max (логотип — круг с вырезом)
function MaxIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 100 100" fill="currentColor">
      <path d="M50.76 0.26c27.53 0 49.13 22.34 49.13 49.89 0 27.55-22.28 49.34-48.86 49.34-9.44 0-14.01-1.33-21.37-6.54-.51-.36-1.2-.26-1.63.2-5.66 6.04-20.17 10.28-20.83 2.03 0-14.39-7.19-23.73-7.19-45.3C0 21.55 23.22 0.26 50.76 0.26zm.77 24.55c-13.07-.69-23.26 8.39-25.51 22.57-1.87 11.75 1.43 26.07 4.26 26.79 1.2.31 4.08-1.9 6.18-3.87.4-.37 1-.44 1.46-.15 3.27 2 6.97 3.5 11.05 3.72 13.41.7 25.3-9.8 26.01-23.21.7-13.41-10.02-25.14-23.43-25.84h-.02z" />
    </svg>
  );
}
import { useApp } from '../../context/AppContext';
import client from '../../api/client';
import MergeRequestBanner from '../../components/MergeRequestBanner';
import { toast } from '../../components/Toast/toast';

// Сотрудники загружаются из API
const FALLBACK_TEAM = [
  { name: 'Елена', role: 'Главный садовод', description: 'Заботится о плодовых деревьях и кустарниках уже более 15 лет.' },
  { name: 'Андрей', role: 'Специалист по хвойным', description: 'Выращивает туи, ели и можжевельники с особым вниманием.' },
  { name: 'Мария', role: 'Флорист-дизайнер', description: 'Создаёт красивые композиции из цветов и декоративных растений.' },
];

// Расчёт статуса работы
function getWorkStatus() {
  const now = new Date();
  const hours = now.getHours();
  const minutes = now.getMinutes();
  const currentMinutes = hours * 60 + minutes;
  const openTime = 9 * 60;
  const closeTime = 19 * 60;

  if (currentMinutes >= openTime && currentMinutes < closeTime) {
    const minutesLeft = closeTime - currentMinutes;
    const hoursLeft = Math.floor(minutesLeft / 60);
    const minsLeft = minutesLeft % 60;
    if (hoursLeft > 0) {
      return { isOpen: true, text: `Закроется через ${hoursLeft} ч ${minsLeft} мин` };
    }
    return { isOpen: true, text: `Закроется через ${minsLeft} мин` };
  } else {
    let minutesUntilOpen;
    if (currentMinutes < openTime) {
      minutesUntilOpen = openTime - currentMinutes;
    } else {
      minutesUntilOpen = (24 * 60 - currentMinutes) + openTime;
    }
    const hoursUntil = Math.floor(minutesUntilOpen / 60);
    const minsUntil = minutesUntilOpen % 60;
    if (hoursUntil > 0) {
      return { isOpen: false, text: `Откроется через ${hoursUntil} ч ${minsUntil} мин` };
    }
    return { isOpen: false, text: `Откроется через ${minsUntil} мин` };
  }
}

// ═══════════════════════════════════════════
// Подстраница: О садовом центре
// ═══════════════════════════════════════════
function GardenCenterPage({ onBack }) {
  const [workStatus, setWorkStatus] = useState(getWorkStatus());
  const [teamMembers, setTeamMembers] = useState(FALLBACK_TEAM);
  const [phoneCopied, setPhoneCopied] = useState(false);

  const copyPhone = () => {
    navigator.clipboard?.writeText(GARDEN_CENTER.phone)
      .then(() => {
        setPhoneCopied(true);
        setTimeout(() => setPhoneCopied(false), 2000);
      })
      .catch(() => {});
  };

  useEffect(() => {
    const interval = setInterval(() => setWorkStatus(getWorkStatus()), 60000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    client.get('/team/')
      .then(res => { if (res.data?.length > 0) setTeamMembers(res.data); })
      .catch(() => {});
  }, []);

  return (
    <div className="animate-fade-in">
      <header className="sticky top-0 z-30 bg-cream/85 px-4 pb-3 pt-4 backdrop-blur-xl">
        <div className="flex items-center gap-3">
          <button onClick={onBack} className="grid h-9 w-9 place-items-center rounded-xl bg-cream-card text-green-muted">
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div className="text-base font-bold text-green-dark">О садовом центре</div>
        </div>
      </header>

      <div className="px-4 py-4 space-y-3">

        {/* Адрес */}
        <div className="rounded-2xl bg-cream-card ring-1 ring-border-warm p-4">
          <div className="flex items-center gap-3">
            <div className="grid h-10 w-10 place-items-center rounded-xl bg-green-sage/20 text-green-primary">
              <MapPin className="h-5 w-5" />
            </div>
            <div>
              <div className="text-sm font-medium text-green-dark">Адрес</div>
              <div className="text-xs text-green-muted">{GARDEN_CENTER.address}</div>
            </div>
          </div>
        </div>

        {/* Часы работы */}
        <div className="rounded-2xl bg-cream-card ring-1 ring-border-warm p-4">
          <div className="flex items-center gap-3 mb-3">
            <div className="grid h-10 w-10 place-items-center rounded-xl bg-yellow-soft/20 text-terracotta">
              <Timer className="h-5 w-5" />
            </div>
            <div>
              <div className="text-sm font-medium text-green-dark">Часы работы</div>
              <div className="text-xs text-green-muted">{GARDEN_CENTER.workingHours}</div>
            </div>
          </div>
          <div className="flex justify-center">
            <div className={`inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-semibold ${
              workStatus.isOpen ? 'bg-green-sage/20 text-green-primary' : 'bg-[#D64545]/10 text-[#D64545]'
            }`}>
              <div className={`h-2 w-2 rounded-full ${workStatus.isOpen ? 'bg-green-primary' : 'bg-[#D64545]'}`} />
              {workStatus.isOpen ? 'Открыто' : 'Закрыто'} · {workStatus.text}
            </div>
          </div>
        </div>

        {/* Телефон + Написать нам */}
        <div className="rounded-2xl bg-cream-card ring-1 ring-border-warm p-4">
          <div className="flex items-center gap-3 mb-4">
            <div className="grid h-10 w-10 place-items-center rounded-xl bg-green-sage/20 text-green-primary">
              <Phone className="h-5 w-5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-green-dark">Телефон</div>
              <div className="text-xs text-green-muted">{GARDEN_CENTER.phone}</div>
            </div>
            <button
              onClick={copyPhone}
              className="flex-shrink-0 rounded-lg bg-cream px-3 py-1.5 text-xs font-medium text-green-primary hover:bg-green-sage/10 transition active:scale-95"
            >
              {phoneCopied ? 'Скопировано!' : 'Копировать'}
            </button>
          </div>
          <div className="text-xs font-medium text-green-muted mb-2">Написать нам:</div>
          <div className="flex gap-2">
            <a
              href={GARDEN_CENTER.telegramUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-[#229ED9] py-2.5 text-sm font-semibold text-white active:scale-[0.98]"
            >
              <Send className="h-4 w-4" />
              Telegram
            </a>
            <a
              href={GARDEN_CENTER.maxUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-[#471AFF] to-[#9500FF] py-2.5 text-sm font-semibold text-white active:scale-[0.98]"
            >
              <MaxIcon className="h-4 w-4" />
              Max
            </a>
          </div>
        </div>

        {/* Как проехать */}
        <div className="rounded-2xl bg-cream-card ring-1 ring-border-warm p-4">
          <div className="flex items-center gap-3 mb-3">
            <div className="grid h-10 w-10 place-items-center rounded-xl bg-green-sage/20 text-green-primary">
              <MapPin className="h-5 w-5" />
            </div>
            <div className="text-sm font-medium text-green-dark">Как проехать</div>
          </div>
          <a
            href={GARDEN_CENTER.yandexMapsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-green-primary py-2.5 text-sm font-semibold text-white active:scale-[0.98]"
          >
            <MapPin className="h-4 w-4" />
            Построить маршрут
            <ExternalLink className="h-3 w-3" />
          </a>
        </div>

        {/* О нас + команда */}
        <div className="rounded-2xl bg-cream-card ring-1 ring-border-warm p-4">
          <div className="flex items-center gap-3 mb-3">
            <div className="grid h-10 w-10 place-items-center rounded-xl bg-green-sage/20 text-green-primary">
              <Leaf className="h-5 w-5" />
            </div>
            <div className="text-sm font-medium text-green-dark">О нас</div>
          </div>
          <p className="text-xs text-green-muted mb-4 leading-relaxed">
            {GARDEN_CENTER.about}
          </p>

          {/* Карточки сотрудников */}
          <div className="space-y-3 mb-4">
            {teamMembers.map((member, index) => (
              <div key={index} className="flex items-start gap-3 rounded-xl bg-cream p-3">
                <div className="h-20 w-20 flex-shrink-0 overflow-hidden rounded-xl bg-gradient-to-br from-green-sage to-green-primary">
                  {member.photo_url ? (
                    <img src={member.photo_url} alt={member.name} className="h-full w-full object-cover" />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center text-white text-2xl font-bold">
                      {member.name?.[0] || '?'}
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-green-dark">{member.name}</div>
                  <div className="text-[11px] text-green-primary font-medium mb-1">{member.role}</div>
                  <div className="text-xs text-green-muted">{member.description}</div>
                </div>
              </div>
            ))}
          </div>

          <a
            href={GARDEN_CENTER.yandexReviewsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-terracotta py-2.5 text-sm font-semibold text-white active:scale-[0.98]"
          >
            <Heart className="h-4 w-4" />
            Оставить отзыв
            <ExternalLink className="h-3 w-3" />
          </a>
        </div>

        {/* Подписки */}
        <div className="rounded-2xl bg-cream-card ring-1 ring-border-warm overflow-hidden">
          <div className="px-4 pt-4 pb-2 flex items-center gap-3">
            <div className="grid h-10 w-10 place-items-center rounded-xl bg-green-sage/20 text-green-primary">
              <BellRing className="h-5 w-5" />
            </div>
            <div className="text-sm font-medium text-green-dark">Подписаться на нас</div>
          </div>
          <div className="px-4 pb-4 space-y-2">
            <a
              href={GARDEN_CENTER.telegramChannelUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 rounded-xl bg-cream p-3 active:scale-[0.98]"
            >
              <div className="grid h-9 w-9 place-items-center rounded-lg bg-[#229ED9] text-white flex-shrink-0">
                <Send className="h-4 w-4" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-green-dark">Telegram канал</div>
                <div className="text-xs text-green-muted">Новости и акции</div>
              </div>
              <ExternalLink className="h-4 w-4 text-border-warm flex-shrink-0" />
            </a>

            <a
              href={GARDEN_CENTER.maxChannelUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 rounded-xl bg-cream p-3 active:scale-[0.98]"
            >
              <div className="grid h-9 w-9 place-items-center rounded-lg bg-gradient-to-r from-[#471AFF] to-[#9500FF] text-white flex-shrink-0">
                <MaxIcon className="h-4 w-4" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-green-dark">Max канал</div>
                <div className="text-xs text-green-muted">Новости и обновления</div>
              </div>
              <ExternalLink className="h-4 w-4 text-border-warm flex-shrink-0" />
            </a>

            <a
              href={GARDEN_CENTER.websiteUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 rounded-xl bg-cream p-3 active:scale-[0.98]"
            >
              <div className="grid h-9 w-9 place-items-center rounded-lg bg-green-primary text-white flex-shrink-0">
                <Leaf className="h-4 w-4" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-green-dark">Наш сайт</div>
                <div className="text-xs text-green-muted">sova-sad-centr.ru</div>
              </div>
              <ExternalLink className="h-4 w-4 text-border-warm flex-shrink-0" />
            </a>
          </div>
        </div>

        {/* Реквизиты */}
        <div className="rounded-2xl bg-cream-card ring-1 ring-border-warm p-4">
          <div className="flex items-center gap-3 mb-3">
            <div className="grid h-10 w-10 place-items-center rounded-xl bg-green-sage/20 text-green-primary">
              <Building2 className="h-5 w-5" />
            </div>
            <div className="text-sm font-medium text-green-dark">Реквизиты</div>
          </div>
          <div className="space-y-2 text-xs text-green-muted">
            <div>
              <div className="font-medium text-green-dark">Индивидуальный предприниматель</div>
              <div>Пономаренко Александр Николаевич</div>
            </div>
            <div className="flex gap-4">
              <div>ИНН: 772157869068</div>
              <div>ОГРН: 321508100008390</div>
            </div>
            <div className="pt-2 border-t border-cream">
              <div className="font-medium text-green-dark">Банк</div>
              <div>ПАО «СБЕРБАНК» г. Щелково</div>
              <div>БИК: 044525225</div>
              <div>Корр. счет: 30101810400000000225</div>
              <div>Р/С: 40802810240000155984</div>
            </div>
            <div className="pt-2 border-t border-cream">
              <div className="font-medium text-green-dark">Юридический адрес</div>
              <div>117519, город Москва, вн. тер. г. Муниципальный округ Чертаново Центральное, ул. Красного Маяка, д. 4, к. 3</div>
            </div>
          </div>
        </div>

        <div className="h-4" />
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════
// Подстраница: Мои данные (редактирование)
// ═══════════════════════════════════════════
function MyDataPage({ onBack, onSaved }) {
  const { user } = useApp();
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const [profile, setProfile] = useState({
    first_name: user?.first_name || '',
    phone: '',
    city: '',
    street: '',
    house: '',
    flat: '',
  });

  const [form, setForm] = useState({ ...profile });
  const [mergeState, setMergeState] = useState(null); // null | 'pending' | 'rejected' | 'expired'
  const [mergePhone, setMergePhone] = useState('');

  useEffect(() => {
    import('../../api/client').then(({ default: client }) => {
      client.get('/users/me')
        .then(res => {
          const parts = (res.data.address || '').split(',').map(s => s.trim());
          const data = {
            first_name: res.data.first_name || '',
            phone: res.data.phone || '',
            city: parts[0] || '',
            street: parts[1] || '',
            house: parts[2] || '',
            flat: parts[3] || '',
          };
          setProfile(data);
          setForm(data);
        })
        .catch(() => {});
    });
  }, []);

  // Есть активный запрос — сразу экран ожидания
  useEffect(() => {
    client.get('/users/me/merge-requests/mine')
      .then(res => {
        if (res.data.status === 'pending') {
          setMergePhone(res.data.phone || '');
          setMergeState('pending');
        }
      })
      .catch(() => {});
  }, []);

  // Опрос статуса запроса каждые 3 секунды
  useEffect(() => {
    if (mergeState !== 'pending') return;
    const interval = setInterval(() => {
      client.get('/users/me/merge-requests/mine')
        .then(res => {
          const s = res.data.status;
          if (s === 'confirmed' && res.data.token) {
            clearInterval(interval);
            localStorage.setItem('sova-token', res.data.token);
            window.location.reload();
          } else if (s === 'rejected') {
            clearInterval(interval);
            setMergeState('rejected');
          } else if (s === 'expired') {
            clearInterval(interval);
            setMergeState('expired');
          }
        })
        .catch(() => {});
    }, 3000);
    return () => clearInterval(interval);
  }, [mergeState]);

  // Сообщения после завершения опроса
  useEffect(() => {
    if (mergeState === 'rejected') {
      toast.info('Владелец телефона отклонил запрос на объединение.');
      setMergeState(null);
    } else if (mergeState === 'expired') {
      toast.info('Срок подтверждения запроса истёк. Отправьте запрос заново.');
      setMergeState(null);
    }
  }, [mergeState]);

  const handleEdit = () => {
    setForm({ ...profile });
    setEditing(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const client = (await import('../../api/client')).default;
      const address = [form.city, form.street, form.house, form.flat]
        .filter(Boolean)
        .join(', ');
      const res = await client.put('/users/me', {
        first_name: form.first_name,
        phone: normalizePhone(form.phone),
        address: address,
      });
      if (res.data.merge_request === 'pending') {
        setMergePhone(normalizePhone(form.phone));
        setMergeState('pending');
        setEditing(false);
        onSaved?.();
        return;
      }
      setProfile({ ...form });
      setEditing(false);
      setSaved(true);
      onSaved?.();
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ошибка сохранения');
    } finally {
      setSaving(false);
    }
  };

  const addressText = [profile.city, profile.street].filter(Boolean).join(', ')
    + (profile.house ? `, д. ${profile.house}` : '')
    + (profile.flat ? `, кв. ${profile.flat}` : '');

  if (mergeState === 'pending') {
    return (
      <div className="animate-fade-in">
        <header className="sticky top-0 z-30 bg-cream/85 px-4 pb-3 pt-4 backdrop-blur-xl">
          <div className="flex items-center gap-3">
            <button onClick={onBack} className="grid h-9 w-9 place-items-center rounded-xl bg-cream-card text-green-muted">
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div className="text-base font-bold text-green-dark">Запрос на объединение</div>
          </div>
        </header>
        <div className="px-4 py-4 space-y-4">
          <div className="rounded-2xl bg-cream-card p-6 ring-1 ring-border-warm space-y-3 text-center">
            <CheckCircle className="mx-auto h-10 w-10 text-green-primary" />
            <div className="text-sm font-semibold text-green-dark">Запрос отправлен</div>
            <div className="text-xs text-green-muted leading-relaxed">
              Теперь попросите владельца номера{' '}
              <span className="font-semibold text-green-dark">{mergePhone}</span> открыть
              наш магазин в Telegram (или MAX) и нажать «Подтвердить» на жёлтой плашке
              вверху экрана. На подтверждение — 15 минут. Как только он подтвердит,
              данные объединятся автоматически, экран обновится сам.
            </div>
            <div className="text-[11px] text-green-muted">Обновление статуса — автоматически каждые 3 секунды</div>
          </div>
          <button
            onClick={onBack}
            className="w-full rounded-xl border border-border-warm py-3 text-sm font-medium text-green-muted hover:bg-cream"
          >
            К профилю
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="animate-fade-in">
      <header className="sticky top-0 z-30 bg-cream/85 px-4 pb-3 pt-4 backdrop-blur-xl">
        <div className="flex items-center gap-3">
          <button onClick={onBack} className="grid h-9 w-9 place-items-center rounded-xl bg-cream-card text-green-muted">
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div className="text-base font-bold text-green-dark">Мои данные</div>
        </div>
      </header>
      <div className="px-4 py-4 space-y-4">

        {!editing ? (
          <div className="rounded-2xl bg-cream-card ring-1 ring-border-warm overflow-hidden">
            <div className="divide-y divide-cream">
              <div className="flex items-center gap-3 px-4 py-3.5">
                <div className="grid h-9 w-9 place-items-center rounded-xl bg-green-sage/20 text-green-primary">
                  <UserRound className="h-4 w-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[11px] text-green-muted">Имя</div>
                  <div className="text-sm font-medium text-green-dark">{profile.first_name || '—'}</div>
                </div>
              </div>
              <div className="flex items-center gap-3 px-4 py-3.5">
                <div className="grid h-9 w-9 place-items-center rounded-xl bg-green-sage/20 text-green-primary">
                  <Smartphone className="h-4 w-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[11px] text-green-muted">Телефон</div>
                  <div className="text-sm font-medium text-green-dark">{profile.phone || '—'}</div>
                </div>
              </div>
              <div className="flex items-center gap-3 px-4 py-3.5">
                <div className="grid h-9 w-9 place-items-center rounded-xl bg-green-sage/20 text-green-primary">
                  <Home className="h-4 w-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[11px] text-green-muted">Адрес доставки</div>
                  <div className="text-sm font-medium text-green-dark">{addressText || 'Не указан'}</div>
                </div>
              </div>
            </div>
            <div className="p-4 pt-2">
              <button
                onClick={handleEdit}
                className="w-full flex items-center justify-center gap-2 rounded-xl bg-cream py-3 text-sm font-semibold text-green-dark hover:bg-green-sage/10 transition active:scale-[0.98]"
              >
                <Pencil className="h-4 w-4" />
                Изменить данные
              </button>
            </div>
          </div>
        ) : (
          <div className="rounded-2xl bg-cream-card p-4 ring-1 ring-border-warm">
            <h3 className="text-sm font-semibold text-green-dark mb-3">Редактирование</h3>
            <div className="space-y-3">
              <div>
                <label className="text-xs text-green-muted mb-1 block">Имя</label>
                <input
                  type="text"
                  value={form.first_name}
                  onChange={e => setForm({ ...form, first_name: e.target.value })}
                  className="w-full rounded-xl border border-border-warm bg-cream px-3 py-2.5 text-sm outline-none focus:border-green-sage focus:bg-white"
                  placeholder="Ваше имя"
                />
              </div>
              <div>
                <label className="text-xs text-green-muted mb-1 block">Телефон</label>
                <input
                  type="tel"
                  value={form.phone}
                  onChange={e => setForm({ ...form, phone: cleanPhoneInput(e.target.value) })}
                  onBlur={() => setForm(prev => ({ ...prev, phone: formatPhoneDisplay(prev.phone) }))}
                  className="w-full rounded-xl border border-border-warm bg-cream px-3 py-2.5 text-sm outline-none focus:border-green-sage focus:bg-white"
                  placeholder="+7 (999) 123-45-67"
                />
                {/* Подсказка про объединение: видна всё время редактирования,
                    рядом с полем телефона, чтобы пользователь знал о последствиях
                    до нажатия «Сохранить» (спокойный тон, без запугивания) */}
                <div className="mt-1.5 rounded-xl bg-yellow-soft/30 px-3 py-2 text-xs leading-relaxed text-green-dark ring-1 ring-yellow-soft">
                  Если номер уже используется в приложении, после сохранения отправится запрос на объединение — подтвердите его в том профиле, и все заказы и данные останутся в одном месте.
                </div>
              </div>
              <div className="pt-2 border-t border-cream">
                <label className="text-xs font-medium text-green-dark mb-2 block">Адрес доставки</label>
                <div className="space-y-2">
                  <input type="text" value={form.city} onChange={e => setForm({ ...form, city: e.target.value })} className="w-full rounded-xl border border-border-warm bg-cream px-3 py-2.5 text-sm outline-none focus:border-green-sage focus:bg-white" placeholder="Город" />
                  <input type="text" value={form.street} onChange={e => setForm({ ...form, street: e.target.value })} className="w-full rounded-xl border border-border-warm bg-cream px-3 py-2.5 text-sm outline-none focus:border-green-sage focus:bg-white" placeholder="Улица" />
                  <div className="grid grid-cols-2 gap-2">
                    <input type="text" value={form.house} onChange={e => setForm({ ...form, house: e.target.value })} className="w-full rounded-xl border border-border-warm bg-cream px-3 py-2.5 text-sm outline-none focus:border-green-sage focus:bg-white" placeholder="Дом" />
                    <input type="text" value={form.flat} onChange={e => setForm({ ...form, flat: e.target.value })} className="w-full rounded-xl border border-border-warm bg-cream px-3 py-2.5 text-sm outline-none focus:border-green-sage focus:bg-white" placeholder="Квартира" />
                  </div>
                </div>
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <button onClick={() => setEditing(false)} className="flex-1 rounded-xl border border-border-warm py-3 text-sm font-medium text-green-muted hover:bg-cream">Отмена</button>
              <button onClick={handleSave} disabled={saving} className="flex-1 rounded-xl bg-terracotta py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-terracotta/90 active:scale-[0.98] disabled:opacity-50">
                {saving ? 'Сохранение...' : 'Сохранить'}
              </button>
            </div>
          </div>
        )}

        {saved && (
          <div className="rounded-xl bg-green-sage/20 p-3 text-sm text-green-primary text-center font-medium">
            Данные сохранены!
          </div>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════
// Подстраница: Заказы
// ═══════════════════════════════════════════
const ORDER_STATUSES = {
  pending: { text: ORDER_STATUS_LABELS.pending, color: 'bg-yellow-soft/20 text-terracotta ring-yellow-soft', icon: '⏳' },
  paid: { text: ORDER_STATUS_LABELS.paid, color: 'bg-green-sage/20 text-green-primary ring-green-sage', icon: '✓' },
  preparing: { text: ORDER_STATUS_LABELS.preparing, color: 'bg-blue-50 text-blue-600 ring-blue-200', icon: '📦' },
  ready: { text: ORDER_STATUS_LABELS.ready, color: 'bg-purple-50 text-purple-600 ring-purple-200', icon: '✨' },
  delivered: { text: ORDER_STATUS_LABELS.delivered, color: 'bg-cream text-green-muted ring-border-warm', icon: '🚚' },
  cancelled: { text: ORDER_STATUS_LABELS.cancelled, color: 'bg-[#D64545]/10 text-[#D64545] ring-[#D64545]/30', icon: '✕' },
  reserved: { text: ORDER_STATUS_LABELS.reserved, color: 'bg-yellow-soft/20 text-terracotta ring-yellow-soft', icon: '⏱' },
};

function OrdersPage({ onBack }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    setLoadError('');
    import('../../api/client').then(({ default: client }) => {
      client.get('/orders')
        .then(res => setOrders(res.data || []))
        .catch(() => setLoadError('Не удалось загрузить заказы'))
        .finally(() => setLoading(false));
    });
  }, []);

  return (
    <div className="animate-fade-in">
      <header className="sticky top-0 z-30 bg-cream/85 px-4 pb-3 pt-4 backdrop-blur-xl">
        <div className="flex items-center gap-3">
          <button onClick={onBack} className="grid h-9 w-9 place-items-center rounded-xl bg-cream-card text-green-muted">
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <div className="text-[11px] uppercase tracking-widest text-green-muted">Заказы</div>
            <div className="-mt-0.5 text-base font-bold text-green-dark">История заказов</div>
          </div>
        </div>
      </header>
      <div className="px-4 py-4">
        {loadError && (
          <div className="mb-3 rounded-xl bg-[#D64545]/10 p-4 text-center text-sm text-[#D64545]">
            {loadError}
          </div>
        )}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-green-primary"></div>
          </div>
        ) : orders.length > 0 ? (
          <div className="space-y-3">
            {orders.map(order => {
              const status = ORDER_STATUSES[order.status] || { text: order.status, color: 'bg-cream text-green-muted ring-border-warm', icon: '?' };
              return (
                <div key={order.id} className="rounded-2xl bg-cream-card ring-1 ring-border-warm overflow-hidden">
                  <div className="flex items-center justify-between px-4 pt-4 pb-2">
                    <div>
                      <span className="text-sm font-bold text-green-dark">#{String(order.id).slice(-5)}</span>
                      <span className="text-xs text-green-muted ml-2">{formatDateShortTime(order.created_at)}</span>
                    </div>
                    <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-medium ring-1 ${status.color}`}>
                      <span>{status.icon}</span>
                      {status.text}
                    </span>
                  </div>
                  {order.items?.length > 0 && (
                    <div className="px-4 pb-2">
                      <div className="space-y-1">
                        {order.items.slice(0, 3).map((item, i) => (
                          <div key={i} className="flex items-center justify-between text-xs">
                            <span className="text-green-muted truncate flex-1 mr-2">
                              {item.product_name || 'Товар'}{item.variant_label ? `, ${item.variant_label}` : ''}
                              {item.quantity > 1 && <span className="text-border-warm"> ×{item.quantity}</span>}
                            </span>
                            <span className="text-green-dark font-medium shrink-0">{formatPrice(Number(item.price * item.quantity))} ₽</span>
                          </div>
                        ))}
                        {order.items.length > 3 && (
                          <div className="text-[11px] text-green-muted">и ещё {order.items.length - 3} товар(ов)</div>
                        )}
                      </div>
                    </div>
                  )}
                  <div className="flex items-center justify-between px-4 py-3 bg-cream border-t border-cream">
                    {order.items?.length > 0 && (
                      <span className="text-xs text-green-muted">{order.items.length} товар(ов)</span>
                    )}
                    <span className={`text-sm font-bold text-green-dark ${!order.items?.length ? 'ml-auto' : ''}`}>{formatPrice(Number(order.total_amount))} ₽</span>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="rounded-2xl bg-cream-card py-16 text-center text-sm text-green-muted">
            <ShoppingBag className="mx-auto mb-2 h-8 w-8" />
            <p>Заказов пока нет</p>
            <p className="mt-1 text-xs">Оформите первый заказ в каталоге</p>
          </div>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════
// Подстраница: Уведомления
// ═══════════════════════════════════════════
function NotificationsPage({ onBack }) {
  const { notifications, toggleNotification } = useApp();
  return (
    <div className="animate-fade-in">
      <header className="sticky top-0 z-30 bg-cream/85 px-4 pb-3 pt-4 backdrop-blur-xl">
        <div className="flex items-center gap-3">
          <button onClick={onBack} className="grid h-9 w-9 place-items-center rounded-xl bg-cream-card text-green-muted">
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <div className="text-[11px] uppercase tracking-widest text-green-muted">Уведомления</div>
            <div className="-mt-0.5 text-base font-bold text-green-dark">О поступлении товаров</div>
          </div>
        </div>
      </header>
      <div className="px-4 py-4">
        {notifications.length === 0 ? (
          <div className="rounded-2xl bg-cream-card py-16 text-center text-sm text-green-muted">
            <BellRing className="mx-auto mb-2 h-8 w-8" />
            <p>Нет активных подписок</p>
            <p className="mt-1 text-xs">Подпишитесь на товар, чтобы узнать о поступлении</p>
          </div>
        ) : (
          <div className="space-y-2">
            {notifications.map(product => (
              <div key={product.id} className="flex items-center gap-3 rounded-2xl bg-cream-card p-3 ring-1 ring-border-warm">
                <div className="h-14 w-14 flex-shrink-0 overflow-hidden rounded-xl bg-cream">
                  {product.image_url ? (
                    <img src={product.image_url} alt={product.name} className="h-full w-full object-cover" />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center text-2xl">🌱</div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-green-dark truncate">{product.name}</div>
                  {product.price > 0 ? (
                    <div className="flex flex-wrap items-center gap-1.5">
                      {hasDiscount(product) && (
                        <>
                          <span className="text-xs text-green-muted line-through">
                            {formatPrice(priceBefore(product.price, product.discount_percent))} ₽
                          </span>
                          <span className="rounded-full bg-[#D64545]/15 px-1.5 py-0.5 text-[10px] font-semibold text-[#D64545]">
                            −{product.discount_percent}%
                          </span>
                        </>
                      )}
                      <span className="text-xs font-bold text-green-dark">{formatPrice(product.price)} ₽</span>
                    </div>
                  ) : (
                    <div className="text-xs text-green-muted">Ожидается</div>
                  )}
                </div>
                <button
                  onClick={() => toggleNotification(product)}
                  className="flex-shrink-0 rounded-lg bg-cream px-3 py-1.5 text-xs font-medium text-green-muted hover:bg-green-sage/10"
                >
                  Отменить
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════
// Подстраница: Оптовые заказы
// ═══════════════════════════════════════════
function WholesalePage({ onBack }) {
  const { user } = useApp();
  const [form, setForm] = useState({
    name: '',
    phone: '',
    comment: '',
  });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  // Авто-подстановка данных из профиля (актуальное имя и телефон)
  useEffect(() => {
    import('../../api/client').then(({ default: client }) => {
      client.get('/users/me')
        .then(res => {
          setForm(f => ({
            ...f,
            name: res.data.first_name || f.name,
            phone: res.data.phone || f.phone,
          }));
        })
        .catch(() => {});
    });
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !isPhoneComplete(form.phone)) return;
    setSending(true);
    try {
      const client = (await import('../../api/client')).default;
      await client.post('/wholesale/', { ...form, phone: normalizePhone(form.phone) });
      setSent(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Не удалось отправить заявку. Попробуйте ещё раз.');
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <div className="animate-fade-in">
        <header className="sticky top-0 z-30 bg-cream/85 px-4 pb-3 pt-4 backdrop-blur-xl">
          <div className="flex items-center gap-3">
            <button onClick={onBack} className="grid h-9 w-9 place-items-center rounded-xl bg-cream-card text-green-muted">
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div className="text-base font-bold text-green-dark">Для заказа оптом</div>
          </div>
        </header>
        <div className="px-4 py-16 text-center">
          <div className="mx-auto mb-4 grid h-16 w-16 place-items-center rounded-full bg-green-sage/20 text-green-primary">
            <CheckCircle className="h-8 w-8" />
          </div>
          <div className="text-lg font-semibold text-green-dark mb-2">Заявка отправлена!</div>
          <p className="text-sm text-green-muted">Мы свяжемся с вами в ближайшее время</p>
          <button
            onClick={onBack}
            className="mt-6 rounded-xl bg-terracotta px-6 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-terracotta/90"
          >
            Вернуться в профиль
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="animate-fade-in">
      <header className="sticky top-0 z-30 bg-cream/85 px-4 pb-3 pt-4 backdrop-blur-xl">
        <div className="flex items-center gap-3">
          <button onClick={onBack} className="grid h-9 w-9 place-items-center rounded-xl bg-cream-card text-green-muted">
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div className="text-base font-bold text-green-dark">Для заказа оптом</div>
        </div>
      </header>
      <div className="px-4 py-4 space-y-3">

        {/* Заголовок с тезисами */}
        <div className="rounded-2xl bg-cream-card ring-1 ring-border-warm p-4">
          <div className="flex items-center gap-3 mb-3">
            <div className="grid h-10 w-10 place-items-center rounded-xl bg-green-sage/20 text-green-primary">
              <Truck className="h-5 w-5" />
            </div>
            <div>
              <h2 className="text-sm font-bold text-green-dark">Для садоводов и компаний</h2>
              <p className="text-xs text-green-muted">Выгодные условия для партнёров</p>
            </div>
          </div>
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-xs text-green-dark">
              <span className="text-green-primary">✓</span> Выгодные цены и гибкая система скидок
            </div>
            <div className="flex items-center gap-2 text-xs text-green-dark">
              <span className="text-green-primary">✓</span> Долгосрочное сотрудничество и поддержка
            </div>
            <div className="flex items-center gap-2 text-xs text-green-dark">
              <span className="text-green-primary">✓</span> Индивидуальный подход к каждому партнёру
            </div>
          </div>
        </div>

        {/* Форма */}
        <div className="rounded-2xl bg-cream-card ring-1 ring-border-warm p-4">
          <h3 className="text-sm font-semibold text-green-dark mb-3">Оставить заявку</h3>
          <form onSubmit={handleSubmit} className="space-y-3">
            <div>
              <label className="text-xs text-green-muted mb-1 block">Ваше имя *</label>
              <input
                type="text"
                value={form.name}
                onChange={e => setForm({ ...form, name: e.target.value })}
                className="w-full rounded-xl border border-border-warm bg-cream px-3 py-2.5 text-sm outline-none focus:border-green-sage focus:bg-white"
                placeholder="Иван"
                required
              />
            </div>
            <div>
              <label className="text-xs text-green-muted mb-1 block">Телефон *</label>
              <input
                type="tel"
                value={form.phone}
                onChange={e => setForm({ ...form, phone: cleanPhoneInput(e.target.value) })}
                onBlur={() => setForm(prev => ({ ...prev, phone: formatPhoneDisplay(prev.phone) }))}
                className="w-full rounded-xl border border-border-warm bg-cream px-3 py-2.5 text-sm outline-none focus:border-green-sage focus:bg-white"
                placeholder="+7 (999) 123-45-67"
                required
              />
            </div>
            <div>
              <label className="text-xs text-green-muted mb-1 block">Что нужно и количество</label>
              <textarea
                value={form.comment}
                onChange={e => setForm({ ...form, comment: e.target.value })}
                className="w-full rounded-xl border border-border-warm bg-cream px-3 py-2.5 text-sm outline-none focus:border-green-sage focus:bg-white resize-none"
                rows={3}
                placeholder="Например: Ель колючая Хупси, 20 штук..."
              />
            </div>
            <button
              type="submit"
              disabled={sending || !form.name.trim() || !isPhoneComplete(form.phone)}
              className="w-full rounded-xl bg-terracotta py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-terracotta/90 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {sending ? 'Отправка...' : 'Отправить заявку'}
            </button>
          </form>
        </div>

        <p className="text-xs text-green-muted text-center px-4">
          Особые условия? Заполните заявку — мы свяжемся с вами и обсудим детали.
        </p>

        <div className="h-4" />
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════
// Основной компонент Профиля
// ═══════════════════════════════════════════
export default function Profile() {
  const location = useLocation();
  const [activePage, setActivePage] = useState(null);
  const { user } = useApp();

  const [profile, setProfile] = useState({
    id: '',
    first_name: user?.first_name || '',
    phone: '',
    address: '',
  });

  // Загружаем данные профиля
  const refreshProfile = () => {
    client.get('/users/me')
      .then(res => {
        setProfile({
          id: res.data.id || '',
          first_name: res.data.first_name || user?.first_name || '',
          phone: res.data.phone || '',
          address: res.data.address || '',
        });
      })
      .catch(() => {});
  };

  useEffect(() => {
    refreshProfile();
  }, [user]);

  // Если пришли с навигацией на оптовую страницу
  useEffect(() => {
    if (location.state?.page === 'wholesale') {
      setActivePage('wholesale');
    }
  }, [location.state]);

  // Рендер подстраницы
  if (activePage === 'garden') return <GardenCenterPage onBack={() => setActivePage(null)} />;
  if (activePage === 'mydata') return <MyDataPage onBack={() => setActivePage(null)} onSaved={refreshProfile} />;
  if (activePage === 'orders') return <OrdersPage onBack={() => setActivePage(null)} />;
  if (activePage === 'notifications') return <NotificationsPage onBack={() => setActivePage(null)} />;
  if (activePage === 'wholesale') return <WholesalePage onBack={() => setActivePage(null)} />;

  return (
    <div className="pb-4 animate-fade-in">
      {/* Шапка */}
      <header className="sticky top-0 z-30 bg-cream/85 px-4 pb-3 pt-4 backdrop-blur-xl">
        <div className="flex items-center gap-2">
          <div className="grid h-9 w-9 place-items-center rounded-xl bg-terracotta text-white">
            <UserRound className="h-5 w-5" />
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-widest text-green-muted">Ваш профиль</div>
            <div className="-mt-0.5 text-base font-bold text-green-dark">Профиль</div>
          </div>
        </div>
      </header>

      <div className="px-4 space-y-3 mt-2">

        {/* Запрос на объединение аккаунтов */}
        <MergeRequestBanner />

        {/* ═══════════════════════════════════════════ */}
        {/* Плитка профиля                              */}
        {/* ═══════════════════════════════════════════ */}
        <div className="rounded-2xl bg-gradient-to-br from-green-primary to-green-sage p-5 text-white shadow-lg">
          <div className="flex items-center gap-4 mb-4">
            {user?.photo_url ? (
              <img src={user.photo_url} alt="" className="h-16 w-16 rounded-full border-2 border-white/30 object-cover" />
            ) : (
              <div className="grid h-16 w-16 place-items-center rounded-full bg-white/20 text-2xl font-bold">
                {(profile.first_name || '?')[0].toUpperCase()}
              </div>
            )}
            <div>
              <div className="text-lg font-bold">{profile.first_name || 'Пользователь'}</div>
              <div className="text-xs text-white/50 mt-0.5">ID: {profile.id || user?.id || '—'}</div>
            </div>
          </div>
          <div className="space-y-2 text-sm">
            <div className="flex items-center gap-2 text-white/80">
              <Smartphone className="h-4 w-4 flex-shrink-0" />
              <span>{profile.phone || 'Телефон не указан'}</span>
            </div>
            <div className="flex items-center gap-2 text-white/80">
              <Home className="h-4 w-4 flex-shrink-0" />
              <span>{profile.address || 'Адрес не указан'}</span>
            </div>
          </div>
          <button
            onClick={() => setActivePage('mydata')}
            className="mt-4 w-full flex items-center justify-center gap-2 rounded-xl bg-white/20 py-2.5 text-sm font-semibold text-white hover:bg-white/30 transition active:scale-[0.98]"
          >
            <Pencil className="h-4 w-4" />
            Изменить данные
          </button>
        </div>

        {/* ═══════════════════════════════════════════ */}
        {/* Заказы, уведомления, опт                    */}
        {/* ═══════════════════════════════════════════ */}
        <div className="rounded-2xl bg-cream-card ring-1 ring-border-warm overflow-hidden">
          <button onClick={() => setActivePage('orders')} className="w-full flex items-center gap-3 px-4 py-3.5 hover:bg-cream transition active:scale-[0.98]">
            <div className="grid h-10 w-10 place-items-center rounded-xl bg-cream text-green-primary">
              <ShoppingBag className="h-5 w-5" />
            </div>
            <div className="flex-1 text-left">
              <div className="text-sm font-medium text-green-dark">Мои заказы</div>
              <div className="text-xs text-green-muted">История и статусы заказов</div>
            </div>
            <ChevronRight className="h-4 w-4 text-border-warm" />
          </button>

          <div className="mx-4 border-t border-cream" />

          <button onClick={() => setActivePage('notifications')} className="w-full flex items-center gap-3 px-4 py-3.5 hover:bg-cream transition active:scale-[0.98]">
            <div className="grid h-10 w-10 place-items-center rounded-xl bg-cream text-green-primary">
              <BellRing className="h-5 w-5" />
            </div>
            <div className="flex-1 text-left">
              <div className="text-sm font-medium text-green-dark">Уведомления о поступлении</div>
              <div className="text-xs text-green-muted">Товары, на которые вы подписались</div>
            </div>
            <ChevronRight className="h-4 w-4 text-border-warm" />
          </button>

          <div className="mx-4 border-t border-cream" />

          <button onClick={() => setActivePage('wholesale')} className="w-full flex items-center gap-3 px-4 py-3.5 hover:bg-cream transition active:scale-[0.98]">
            <div className="grid h-10 w-10 place-items-center rounded-xl bg-cream text-green-primary">
              <Truck className="h-5 w-5" />
            </div>
            <div className="flex-1 text-left">
              <div className="text-sm font-medium text-green-dark">Для заказа оптом</div>
              <div className="text-xs text-green-muted">Специальные условия для опта</div>
            </div>
            <ChevronRight className="h-4 w-4 text-border-warm" />
          </button>
        </div>

        {/* ═══════════════════════════════════════════ */}
        {/* О садовом центре                            */}
        {/* ═══════════════════════════════════════════ */}
        <button
          onClick={() => setActivePage('garden')}
          className="w-full rounded-2xl bg-cream-card ring-1 ring-border-warm p-4 flex items-center gap-3 hover:bg-cream transition active:scale-[0.99]"
        >
          <div className="grid h-10 w-10 place-items-center rounded-xl bg-green-sage/20 text-green-primary">
            <Leaf className="h-5 w-5" />
          </div>
          <div className="flex-1 text-left">
            <div className="text-sm font-medium text-green-dark">О садовом центре</div>
            <div className="text-xs text-green-muted">Адрес, часы работы, контакты</div>
          </div>
          <ChevronRight className="h-4 w-4 text-border-warm" />
        </button>

        <div className="h-4" />
      </div>
    </div>
  );
}
